// src/features/S1Metadata/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Clients } from "../../lib/clients";
import { ClientTables } from "../../lib/clientTables";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  AutocompleteAsyncField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

// Import options from filter.tsx — do not modify filter.tsx
import { fieldKeyOptions, fieldValueOptions } from "./filter";

const DEFAULT_OWNER_TYPE = "Strategy";

// Zod schema for edit form - more permissive during loading
const FormSchema = z.object({
  // Display field for autocomplete
  owner_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
          text: z.string(),
        }),
      ])
      .optional(),
  ),
  // ID field - use coerce with default handling for edit
  owner_id: z.preprocess((val) => {
    if (val === null || val === undefined || isNaN(Number(val))) {
      return undefined;
    }
    return Number(val);
  }, z.number().optional()),
  owner_type: z.string().optional(),
  field_key: z.string().min(1, "Field Key is required"),
  field_value: z.string().optional().nullable(),
});

export function S1MetadataEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "owner_name") {
        const ownerName = fetchedData?.owner_name || value;
        const ownerId = fetchedData?.owner_id;

        if (ownerName && ownerId) {
          return {
            text: ownerName,
            id: ownerId,
            name: ownerName,
          };
        }
        return undefined; // Let autocomplete field stay empty if no data
      }

      if (key === "owner_id") {
        return fetchedData?.owner_id || undefined;
      }

      if (key === "owner_type") {
        return fetchedData?.owner_type || DEFAULT_OWNER_TYPE;
      }

      return value;
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                S1Metadata {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                {/* Row 1: owner fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="owner_name"
                    label={t(`${table_name}.columns.owner_name`, {
                      ns: "tables",
                      defaultValue: "Owner",
                    })}
                    base_url={`${Clients.S1_QUANTILIA}/${ClientTables.S1_META_DATA}`}
                    column_name="name"
                    id_column="owner_id"
                    control={form.control}
                    isRequired={false}
                  />
                  <InputField
                    name="owner_type"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Owner Type",
                    })}
                    isRequired={false}
                    control={form.control}
                  />
                </div>
                {/* Row 2: metadata fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="field_key"
                    label={t(`${table_name}.columns.field_key`, {
                      ns: "tables",
                      defaultValue: "Field Key",
                    })}
                    control={form.control}
                    options={fieldKeyOptions}
                    isRequired
                  />
                  <SelectField
                    name="field_value"
                    label={t(`${table_name}.columns.field_value`, {
                      ns: "tables",
                      defaultValue: "Field Value",
                    })}
                    control={form.control}
                    options={fieldValueOptions}
                    allow_empty_option
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["owner_id"])
              : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel creation modal */}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
